import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Card,
  TextField, Grid, Stack, Switch, FormControlLabel, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Typography, IconButton, Chip, Divider,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import { getCategories } from '../../api/endpoints/categories';
import { uploadFile } from '../../api/endpoints/media';

const MAX_IMAGES = 6;

const initialForm = {
  name: '', shortDescription: '', description: '', category: '',
  weight: '', dimensions: '', material: '',
  isActive: true, isFeatured: false,
};

export default function AddProductModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [sizes, setSizes] = useState([{ price: '', comparePrice: '', sizeLabel: '', stock: '' }]);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setImageFiles([]);
      setImagePreviews([]);
      setSizes([{ price: '', comparePrice: '', sizeLabel: '', stock: '' }]);
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: 'product' });
      setCategories(res.data.data || []);
    } catch { /* ignore */ }
  };

  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSizeChange = (index, field) => (e) => {
    setSizes((prev) => prev.map((s, i) => i === index ? { ...s, [field]: e.target.value } : s));
  };

  const addSize = () => {
    setSizes((prev) => [...prev, { price: '', comparePrice: '', sizeLabel: '', stock: '' }]);
  };

  const removeSize = (index) => {
    setSizes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - imageFiles.length;
    if (remaining <= 0) { toast.warning(`Max ${MAX_IMAGES} images allowed`); return; }
    const toAdd = files.slice(0, remaining);
    setImageFiles((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreviews((prev) => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
    if (files.length > remaining) toast.info(`Only ${remaining} image(s) added, max ${MAX_IMAGES}`);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Product name is required'); return; }
    if (sizes.some((s) => !s.price && s.price !== 0)) { toast.error('Price is required for all sizes'); return; }
    if (!imageFiles.length) { toast.error('At least 1 image is required'); return; }
    setSaving(true);
    try {
      const imageUrls = [];
      if (imageFiles.length) {
        const results = await Promise.all(imageFiles.map((f) => uploadFile(f).then((r) => r.data.data?.url || '')));
        imageUrls.push(...results.filter(Boolean));
      }
      const basePayload = {
        name: form.name,
        shortDescription: form.shortDescription,
        description: form.description,
        category: form.category || undefined,
        weight: form.weight || undefined,
        dimensions: form.dimensions || undefined,
        material: form.material || undefined,
        isActive: form.isActive,
        isFeatured: form.isFeatured,
      };
      if (imageUrls.length) basePayload.images = imageUrls;
      if (sizes.length === 1 && !sizes[0].sizeLabel) {
        const payload = {
          ...basePayload,
          price: Number(sizes[0].price),
          comparePrice: sizes[0].comparePrice ? Number(sizes[0].comparePrice) : undefined,
          stock: Number(sizes[0].stock) || 0,
        };
        await axiosClient.post('/admin/shop/products', payload);
      } else {
        await Promise.all(sizes.map((s) =>
          axiosClient.post('/admin/shop/products', {
            ...basePayload,
            size: s.sizeLabel || undefined,
            price: Number(s.price),
            comparePrice: s.comparePrice ? Number(s.comparePrice) : undefined,
            stock: Number(s.stock) || 0,
          })
        ));
      }
      toast.success(`${sizes.length} product(s) created`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create product(s)');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>Add Product</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Product Name" value={form.name} onChange={handleChange('name')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Short Description" value={form.shortDescription} onChange={handleChange('shortDescription')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={3} size="small" />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={handleChange('category')}>
                <MenuItem value="">None</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField label="Color" value={form.weight} onChange={handleChange('weight')} fullWidth size="small" />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Dimensions" value={form.dimensions} onChange={handleChange('dimensions')} fullWidth size="small" placeholder="e.g. 10x8x2 cm" />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Material" value={form.material} onChange={handleChange('material')} fullWidth size="small" />
          </Grid>
          <Grid item xs={4}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
              <FormControlLabel control={<Switch checked={form.isFeatured} onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))} />} label="Featured" />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={600}>Size Variants</Typography>
              <Button size="small" startIcon={<AddCircleIcon />} onClick={addSize} variant="outlined">Add Size</Button>
            </Stack>
          </Grid>

          {sizes.map((s, i) => (
            <Grid item xs={12} key={i}>
              <Card sx={{ p: 2, bgcolor: '#fafafa' }} variant="outlined">
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Chip label={`Variant ${i + 1}`} size="small" color="primary" variant="outlined" />
                    {sizes.length > 1 && (
                      <IconButton size="small" color="error" onClick={() => removeSize(i)}><DeleteIcon fontSize="small" /></IconButton>
                    )}
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <TextField label="Size Label (e.g. 500g, 1kg)" value={s.sizeLabel} onChange={handleSizeChange(i, 'sizeLabel')} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField label="Price (₹)" type="number" value={s.price} onChange={handleSizeChange(i, 'price')} fullWidth required size="small" />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField label="Compare" type="number" value={s.comparePrice} onChange={handleSizeChange(i, 'comparePrice')} fullWidth size="small" />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField label="Stock" type="number" value={s.stock} onChange={handleSizeChange(i, 'stock')} fullWidth size="small" />
                    </Grid>
                  </Grid>
                </Stack>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={600}>Product Images ({imageFiles.length}/{MAX_IMAGES})</Typography>
              <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />} size="small" disabled={imageFiles.length >= MAX_IMAGES}>
                Upload Images
                <input type="file" hidden accept="image/*" multiple onChange={handleImageSelect} />
              </Button>
            </Stack>
            {imagePreviews.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {imagePreviews.map((preview, i) => (
                  <Box key={i} sx={{ position: 'relative', display: 'inline-block', m: 0.5 }}>
                    <img src={preview} alt={`Preview ${i}`} style={{ width: 80, height: 80, borderRadius: 6, objectFit: 'cover' }} />
                    <IconButton size="small" sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'white' }} onClick={() => removeImage(i)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : `Create ${sizes.length} Product(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
